import type { MeResponse, Permission } from '@platform/contracts';

/**
 * Grupos del menú. "Avanzado" junta lo técnico (estado del sistema, alertas, integraciones,
 * administración): sólo lo ven los roles de agencia, porque piden permisos que un cliente no tiene.
 */
export type ModuleGroup = 'main' | 'setup' | 'advanced';

export const GROUP_LABELS: Record<ModuleGroup, string> = {
  main: 'Tu negocio',
  setup: 'Ajustes',
  advanced: 'Avanzado',
};

export interface ModuleDef {
  id: string;
  path: string;
  /** Nombre en el menú y título de la pantalla. */
  label: string;
  group: ModuleGroup;
  /** Permiso necesario sobre al menos una empresa. `superadmin` = sólo users.is_superadmin. */
  requires: Permission | 'superadmin';
  phase: string;
  /** Qué resuelve la pantalla, en lenguaje llano (subtítulo). */
  description: string;
}

/** Módulos de la consola (§7 del plan). El orden es el del menú. */
export const MODULES: readonly ModuleDef[] = [
  {
    id: 'overview',
    path: '/overview',
    label: 'Inicio',
    group: 'main',
    requires: 'data:read',
    phase: 'F2',
    description: 'Cómo van tus campañas: envíos, respuestas e interesados de un vistazo.',
  },
  {
    id: 'inbox',
    path: '/inbox',
    label: 'Conversaciones',
    group: 'main',
    requires: 'inbox:read',
    phase: 'F3',
    description:
      'Respuestas de clientes que el asistente no pudo contestar y esperan a una persona.',
  },
  {
    id: 'leads',
    path: '/leads',
    label: 'Leads',
    group: 'main',
    requires: 'data:read',
    phase: 'F3',
    description: 'Las tiendas encontradas, si encajan contigo y en qué punto está cada una.',
  },
  {
    id: 'analytics',
    path: '/analitica',
    label: 'Resultados',
    group: 'main',
    requires: 'data:read',
    phase: 'F2',
    description: 'Envíos, aperturas y respuestas día a día, por campaña y por buzón.',
  },
  {
    id: 'history',
    path: '/historial',
    label: 'Historial de emails',
    group: 'main',
    requires: 'data:read',
    phase: 'F2',
    description: 'Todo lo que se le envió a un contacto y lo que respondió, con los adjuntos.',
  },
  {
    id: 'prompts',
    path: '/prompts',
    label: 'Asistente IA',
    group: 'setup',
    requires: 'prompts:read',
    phase: 'F3',
    description:
      'Lo que sabe y cómo escribe el asistente: instrucciones, información y plantillas.',
  },
  {
    id: 'team',
    path: '/equipo',
    label: 'Equipo',
    group: 'setup',
    requires: 'users:invite',
    phase: 'F1',
    description: 'Quién entra al panel y qué puede hacer.',
  },
  {
    id: 'subscription',
    path: '/suscripcion',
    label: 'Plan y facturas',
    group: 'setup',
    requires: 'billing:read',
    phase: 'F3',
    description: 'Tu plan, lo que llevas usado este mes, facturas y pagos.',
  },
  {
    id: 'ops',
    path: '/operaciones',
    label: 'Estado del sistema',
    group: 'advanced',
    requires: 'ops:read',
    phase: 'F2',
    description: 'Ejecuciones de n8n, crons y jobs del worker, con el error legible.',
  },
  {
    id: 'alerts',
    path: '/alertas',
    label: 'Alertas',
    group: 'advanced',
    requires: 'ops:read',
    phase: 'F2',
    description:
      'Avisos técnicos abiertos y resueltos (rebote alto, buzones caídos, procesos parados).',
  },
  {
    id: 'config',
    path: '/config',
    label: 'Integraciones',
    group: 'advanced',
    requires: 'config:read',
    phase: 'F1',
    description: 'Conexiones con Instantly, n8n y bases de datos, API keys, funciones y avisos.',
  },
  {
    id: 'superadmin',
    path: '/superadmin',
    label: 'Administración',
    group: 'advanced',
    requires: 'superadmin',
    phase: 'F1',
    description: 'Clientes, empresas, usuarios, planes, facturas, pagos y márgenes.',
  },
];

export function moduleById(id: string): ModuleDef {
  const mod = MODULES.find((m) => m.id === id);
  if (!mod) throw new Error(`Módulo desconocido: ${id}`);
  return mod;
}

export function canAccess(
  me: Pick<MeResponse, 'user' | 'permissions'>,
  requires: ModuleDef['requires'],
): boolean {
  if (requires === 'superadmin') return me.user.isSuperadmin;
  return me.user.isSuperadmin || me.permissions.includes(requires);
}

export function visibleModules(me: Pick<MeResponse, 'user' | 'permissions'>): ModuleDef[] {
  return MODULES.filter((m) => canAccess(me, m.requires));
}
